<template>
  <section class="my-orders-tab">
    <LcExpandable
      v-model="isActiveExpanded"
      class="pt-050"
      content-class="pt-200"
      :disabled="!showActiveList || isLoadingActive"
    >
      <template #header>{{ $t(`${activePath}.title`) }}</template>

      <div v-if="!isLoadingActive && totalActive === 0" class="pb-100">
        <slot name="no-results" />
      </div>

      <LcRemoteList
        v-else-if="showActiveList"
        :button-text="$t(`${activePath}.button.loadmore`)"
        :count="countActive"
        hide-separator
        :loading="isLoadingActive"
        :total="totalActive"
        @fetch-more="
          fetchMoreActive({
            offset: countActive,
          })
        "
      >
        <slot v-if="resultActive" name="active" :result="resultActive" />
      </LcRemoteList>
    </LcExpandable>

    <LcSeparator
      v-if="showPastList || countActive < totalActive || !isActiveExpanded"
      class="my-200"
    />

    <LcExpandable
      v-if="showPastList"
      v-model="isPastExpanded"
      class="pt-050"
      content-class="pt-200"
      :disabled="isLoadingPast"
    >
      <template #header>{{ $t(`${pastPath}.title`) }}</template>

      <LcRemoteList
        :button-text="$t(`${pastPath}.button.loadmore`)"
        class="pt-100"
        :count="countPast"
        hide-separator
        :loading="isLoadingPast"
        :total="totalPast"
        @fetch-more="
          fetchMorePast({
            offset: countPast,
          })
        "
      >
        <slot v-if="resultPast" name="past" :result="resultPast" />
      </LcRemoteList>
    </LcExpandable>

    <LcSeparator
      v-if="isScrollUpButtonVisible || countPast < totalPast || !isPastExpanded"
      class="my-200"
    />

    <div v-if="isScrollUpButtonVisible" class="flex w-full justify-center">
      <LcButton icon="arrow-up" size="medium" variant="secondary" @click="scrollTop(0)" />
    </div>
  </section>
</template>

<script generic="T extends InferredUseQueryReturn<T>" lang="ts" setup>
type FetchFn<T extends InferredUseQueryReturn<T>> = () => ReturnType<
  typeof useFetchMoreWrapper<T, number>
>

type MyOrdersTabProps<T extends InferredUseQueryReturn<T>> = {
  fetchActive: FetchFn<T>
  fetchPast: FetchFn<T>
  type: string
}

const { fetchActive, fetchPast, type } = defineProps<MyOrdersTabProps<T>>()

const { addToastError } = useToaster()
const { goToSavedScrollPosition, saveScrollPosition, scrollTop } = useScrollUtils()

const isActiveExpanded = ref(true)
const isPastExpanded = ref(true)

const {
  count: countActive,
  error: errorActive,
  fetchMore: fetchMoreActive,
  loading: isLoadingActive,
  onError: onErrorActive,
  refetch: refetchActive,
  result: resultActive,
  total: totalActive,
} = fetchActive()

const {
  count: countPast,
  error: errorPast,
  fetchMore: fetchMorePast,
  loading: isLoadingPast,
  onError: onErrorPast,
  refetch: refetchPast,
  result: resultPast,
  total: totalPast,
} = fetchPast()

const { accessToken } = useAuth()

const { onResult: onOrderStateChange } = useHighFrequencyOrderStateChangesSubscription({
  accessToken,
})

onOrderStateChange(() => {
  if (type === 'high-frequency') {
    refetchActive()
    refetchPast()
  }
})

const activePath = computed(() => `${basePath.value}.active`)
const basePath = computed(() => `myorders.tabs.${type}.section`)
const isScrollUpButtonVisible = computed(() => {
  const visibleActiveOrders = isActiveExpanded.value ? totalActive.value : 0
  const visiblePastOrders = isPastExpanded.value ? totalPast.value : 0
  return visibleActiveOrders + visiblePastOrders >= 6
})
const pastPath = computed(() => `${basePath.value}.past`)
const sessionStorageKey = computed(() => `my_orders_scroll_position_${type}`)
const showActiveList = computed(() => isLoadingActive.value || countActive.value > 0)
const showPastList = computed(() => isLoadingActive.value || countPast.value > 0)

const onError = (error: unknown) => {
  addToastError({
    error,
    prefix: 'myorders.error',
    tracking: {
      topicKey: 'orders_load_failed',
    },
  })
}

onErrorActive(onError)
onErrorPast(onError)
watch(errorActive, () => {
  if (!errorActive.value) return
  errorActive.value.forEach((error) => onError(error))
})

watch(errorPast, () => {
  if (!errorPast.value) return
  errorPast.value.forEach((error) => onError(error))
})

onMounted(async () => {
  const savedScrollPosition = sessionStorage.getItem(sessionStorageKey.value)
  if (!savedScrollPosition) return
  if (isLoadingActive.value || isLoadingPast.value) {
    sessionStorage.removeItem(sessionStorageKey.value)
    return
  }

  await nextTick()
  goToSavedScrollPosition(sessionStorageKey.value)
})

onBeforeRouteLeave(({ path }) => {
  if (!path.match(`/my-orders/${type}/`) || !window?.scrollY) return
  saveScrollPosition(sessionStorageKey.value)
})
</script>
